// src/utils/encryptionUtils.ts 

import { logger } from "./logging/logger";

// --- Decrypt result types (non-breaking wrapper later) ---
export type DecryptOk = { ok: true; text: string };
export type DecryptErr = {
  ok: false;
  code:
    | "PAYLOAD"            // invalid/missing fields
    | "RSA"                // RSA-OAEP failed
    | "RSA_ENVELOPE"       // suspicious RSA ct length
    | "WRONG_DEVICE"       // targeted to another device
    | "KEY_IV"             // invalid key/iv lengths
    | "CT"                 // ciphertext too short
    | "GCM"                // AES-GCM auth/tag failure
    | "LEGACY_NO_ENVELOPE" // old messages without envelope
    | "UNKNOWN";           // unexpected
  reason?: string;         // short reason for logs
};
export type DecryptResult = DecryptOk | DecryptErr;


// --------------------------------------------------------------------------
export const base64ToUint8Array = (base64: string): Uint8Array => {
  try {
    if (!base64 || typeof base64 !== "string") {
      throw new Error("Invalid base64 input: not a string");
    }
    const cleanedBase64 = base64.trim().replace(/\s/g, "");
    const binary = window.atob(cleanedBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch (error: any) {
    // Log minimal, non-sensitive context
    logger.error("base64ToUint8Array failed", {
      error: error?.message,
      input_len: typeof base64 === "string" ? base64.length : 0,
    });
    return new Uint8Array();
  }
};

// base64url -> base64 and pad -----------------------------------------------
const normalizeB64 = (s: string): string => {
  if (typeof s !== "string") throw new Error("b64 not a string");
  let t = s.trim().replace(/\s+/g, "").replace(/-/g, "+").replace(/_/g, "/");
  const m = t.length % 4;
  if (m === 2) t += "==";
  else if (m === 3) t += "=";
  else if (m !== 0) throw new Error("invalid base64 length");
  return t;
};

// robust b64->bytes (throws on error) ----------------------------------------
export const b64ToBytes = (b64: string): Uint8Array => {
  const norm = normalizeB64(b64);
  const bin = atob(norm);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

// ---------------
export const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

// ---------------
export const isBase64 = (str: string): boolean => {
  if (typeof str !== "string") return false;
  const notBase64 = /[^A-Z0-9+\/=]/i;
  const len = str.length;
  if (!len || len % 4 !== 0 || notBase64.test(str)) return false;

  try {
    return btoa(atob(str)) === str;
  } catch {
    return false;
  }
};

// --------------------------------------------------------------------------
// Fix for TS BufferSource typing issues
function viewToArrayBuffer(view: ArrayBufferView): ArrayBuffer {
  const buf = view.buffer as ArrayBuffer; // Cast for TS
  const start = view.byteOffset;
  const end = start + view.byteLength;
  return buf.slice(start, end);
}

export function toArrayBuffer(src: ArrayBuffer | ArrayBufferView): ArrayBuffer {
  return src instanceof ArrayBuffer ? src : viewToArrayBuffer(src);
}


// ---------------------------------------------------------------------
export const encryptMessageWithPublicKey = async (
  message: string,
  publicKeyPem: string
): Promise<string> => {
  const pemBody = publicKeyPem.replace(/-----[^-]+-----/g, "").replace(/\s+/g, "");
  const binaryDer = Uint8Array.from(atob(pemBody), (c) => c.charCodeAt(0));

  const importedPublicKey = await window.crypto.subtle.importKey(
    "spki",
    binaryDer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    true,
    ["encrypt"]
  );

  const encoded = new TextEncoder().encode(message);
  const encrypted = await window.crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    importedPublicKey,
    encoded
  );

  return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
};


// Decrypt Message Locally ----------------------------------------------------------------------
const parseCombined = (u8: Uint8Array) => {
  // key(32) + iv(12) => 44 bytes
  if (u8.byteLength < 44) return { aesKey: null as any, nonce: null as any };
  // key|nonce
  const k1 = u8.slice(0, 32), n1 = u8.slice(32);
  if (n1.byteLength === 12) return { aesKey: k1, nonce: n1 };
  // nonce|key
  const n2 = u8.slice(0, 12), k2 = u8.slice(12, 44);
  if (n2.byteLength === 12 && k2.byteLength === 32) return { aesKey: k2, nonce: n2 };
  return { aesKey: null as any, nonce: null as any };
};

// --------------------
let currentDeviceIdGetter: () => string | null = () => null;
export const setCurrentDeviceIdGetter = (fn: () => string | null) => { currentDeviceIdGetter = fn; };

// --------------------
export const decryptMessageLocallyEx = async (
  base64Message: string,
  privateKey: CryptoKey
): Promise<DecryptResult> => {
  try {
    if (!base64Message || typeof base64Message !== "string") {
      return { ok: false, code: "PAYLOAD", reason: "empty or non-string" };
    }

    // 1) parse outer payload
    const payloadJson = atob(normalizeB64(base64Message));
    const payload = JSON.parse(payloadJson) || {};
    const { encrypted_aes_key, encrypted_message, target_device_id, v } = payload;

    if (!encrypted_aes_key && !encrypted_message) {
      // legacy messages without envelope
      return { ok: false, code: "LEGACY_NO_ENVELOPE", reason: "no envelope fields" };
    }
    if (!encrypted_aes_key || !encrypted_message) {
      return { ok: false, code: "PAYLOAD", reason: "missing fields" };
    }

    // 1.5) device targeting (if available)
    const curDevId = currentDeviceIdGetter?.() ?? null;
    if (target_device_id && curDevId && target_device_id !== curDevId) {
      return { ok: false, code: "WRONG_DEVICE", reason: "target mismatch" };
    }

    // 2) RSA envelope sanity
    const encKey = b64ToBytes(encrypted_aes_key);
    if (encKey.byteLength < 128) {
      // 2048-bit RSA is ~256B; too small is suspicious/invalid
      return { ok: false, code: "RSA_ENVELOPE", reason: `rsa ct too short: ${encKey.byteLength}` };
    }

    // 3) RSA-OAEP decrypt
    let combinedAB: ArrayBuffer;
    try {
      combinedAB = await crypto.subtle.decrypt({ name: "RSA-OAEP" }, privateKey, toArrayBuffer(encKey));
    } catch (e: any) {
      // Log minimal details (no ciphertext)
      // logger.error("RSA-OAEP decrypt failed", { error: e?.message });
      return { ok: false, code: "RSA", reason: e?.message || "rsa-oaep" };
    }

    // 4) split into key/iv
    const { aesKey, nonce } = parseCombined(new Uint8Array(combinedAB));
    if (!aesKey || !nonce || nonce.byteLength !== 12) {
      return { ok: false, code: "KEY_IV", reason: "invalid key/iv lengths" };
    }

    // 5) import AES key
    const aesCryptoKey = await crypto.subtle.importKey(
      "raw",
      toArrayBuffer(aesKey),
      { name: "AES-GCM" },
      false,
      ["decrypt"]
    );

    // 6) decrypt CT with GCM
    const ct = b64ToBytes(encrypted_message);
    if (ct.byteLength < 16) {
      return { ok: false, code: "CT", reason: `ct too short: ${ct.byteLength}` };
    }

    let plainAB: ArrayBuffer;
    try {
      plainAB = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: toArrayBuffer(nonce), tagLength: 128 },
        aesCryptoKey,
        toArrayBuffer(ct)
      );
    } catch (e: any) {
      // Log lengths/metadata only; never content
      logger.error("AES-GCM decrypt failed", {
        error: e?.message,
        ct_len: ct.byteLength,
        iv_len: nonce.byteLength,
        v: v ?? "n/a",
      });
      return { ok: false, code: "GCM", reason: e?.message || "gcm-auth" };
    }

    return { ok: true, text: new TextDecoder().decode(plainAB) };
  } catch (err: any) {
    logger.error("Decryption failed (outer)", { error: err?.message });
    return { ok: false, code: "UNKNOWN", reason: err?.message || "unknown" };
  }
};

// --- Backward compatible wrapper (keeps your old callers working) ---
export const decryptMessageLocally = async (
  base64Message: string,
  privateKey: CryptoKey
): Promise<string> => {
  const r = await decryptMessageLocallyEx(base64Message, privateKey);
  if (r.ok) return r.text;
  // old fallback string
  switch (r.code) {
    case "WRONG_DEVICE": return "🔒 Encrypted for another device";
    case "LEGACY_NO_ENVELOPE": return "🔒 Legacy message (no envelope)";
    case "PAYLOAD": return "🔒 Invalid encrypted message";
    case "RSA_ENVELOPE":
    case "RSA": return "🔒 Decryption failed (RSA)";
    case "KEY_IV": return "🔒 Decryption failed (KEY/IV)";
    case "CT": return "🔒 Decryption failed (CT)";
    case "GCM": return "🔒 Decryption failed (GCM)";
    default: return "🔒 Decryption failed";
  }
};


// Encrypt Message Locally For All Devices -----------------
export const encryptMessageLocallyForAllDevices = async (
  message: string,
  deviceKeys: { device_id: string; public_key: string }[]
): Promise<{ device_id: string; encrypted_content: string }[]> => {
  const encoder = new TextEncoder();
  const encodedMessage = encoder.encode(message);

  // Generate AES key and nonce
  const aesKey = window.crypto.getRandomValues(new Uint8Array(32)); // 256-bit
  const nonce = window.crypto.getRandomValues(new Uint8Array(12)); // AES-GCM standard

  // Import AES key for encryption
  const aesCryptoKey = await window.crypto.subtle.importKey(
    "raw",
    aesKey,
    "AES-GCM",
    false,
    ["encrypt"]
  );

  // Encrypt the message using AES
  const encryptedMessageBuffer = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce },
    aesCryptoKey,
    encodedMessage
  );

  const encryptedMessageBase64 = arrayBufferToBase64(encryptedMessageBuffer);

  // For each device: encrypt the AES key + nonce with RSA public key
  return await Promise.all(
    deviceKeys.map(async ({ device_id, public_key }) => {
      try {
        const cleanedKey = public_key.replace(/-----(BEGIN|END) PUBLIC KEY-----|\n/g, "");
        const binaryDer = base64ToUint8Array(cleanedKey);

        const importedKey = await window.crypto.subtle.importKey(
          "spki",
          toArrayBuffer(binaryDer),
          { name: "RSA-OAEP", hash: "SHA-256" },
          false,
          ["encrypt"]
        );

        // Combine AES key + nonce
        const combined = new Uint8Array(aesKey.length + nonce.length);
        combined.set(aesKey, 0);
        combined.set(nonce, aesKey.length);

        // Encrypt AES key + nonce with RSA
        const encryptedAESBuffer = await window.crypto.subtle.encrypt(
          { name: "RSA-OAEP" },
          importedKey,
          combined
        );

        const encryptedAESKeyBase64 = arrayBufferToBase64(encryptedAESBuffer);

        // Combine both encrypted AES key and AES-encrypted message
        const payload = JSON.stringify({
          encrypted_aes_key: encryptedAESKeyBase64,
          encrypted_message: encryptedMessageBase64,
        });

        const finalBase64 = btoa(payload);

        return {
          device_id,
          encrypted_content: finalBase64,
        };
      } catch (err: any) {
        // Do not leak key or message; log minimal context
        logger.error("Encryption failed for device", {
          device_id,
          error: err?.message,
        });
        return {
          device_id,
          encrypted_content: "[Encryption failed]",
        };
      }
    })
  );
};


// ---------------------------------------------------------------------
export const encryptAESKeyWithPublicKey = async (
  aesKey: Uint8Array,
  nonce: Uint8Array,
  receiverPublicKeyPem: string
): Promise<string> => {
  const pemHeader = "-----BEGIN PUBLIC KEY-----";
  const pemFooter = "-----END PUBLIC KEY-----";
  const pemContents = receiverPublicKeyPem
    .replace(pemHeader, "")
    .replace(pemFooter, "")
    .replace(/\s/g, "");

  const binaryDer = Uint8Array.from(atob(pemContents), (c) => c.charCodeAt(0));

  const publicKey = await window.crypto.subtle.importKey(
    "spki",
    binaryDer.buffer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    true,
    ["encrypt"]
  );

  const combined = new Uint8Array(aesKey.length + nonce.length);
  combined.set(aesKey, 0);
  combined.set(nonce, aesKey.length);

  const encrypted = await window.crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    combined
  );

  return btoa(String.fromCharCode(...new Uint8Array(encrypted))); // Base64 output
};


// ---------------------------------------------------------------------
export const decryptAESKeyWithPrivateKey = async (
  encryptedBase64: string,
  privateKey: CryptoKey
): Promise<{ aesKey: Uint8Array; nonce: Uint8Array }> => {
  const encryptedBytes = Uint8Array.from(atob(encryptedBase64), (c) => c.charCodeAt(0));
  const decrypted = await window.crypto.subtle.decrypt(
    { name: "RSA-OAEP" },
    privateKey,
    encryptedBytes
  );

  const combined = new Uint8Array(decrypted);
  const aesKey = combined.slice(0, 32);
  const nonce = combined.slice(32);
  return { aesKey, nonce };
};


// ---------------------------------------------------------------------
export const decryptFileWithAES = async (
  encryptedBytes: Uint8Array,
  aesKey: Uint8Array,
  nonce: Uint8Array
): Promise<ArrayBuffer> => {
  try {
    const aesCryptoKey = await window.crypto.subtle.importKey(
      "raw",
      toArrayBuffer(aesKey),
      { name: "AES-GCM" },
      false,
      ["decrypt"]
    );

    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: toArrayBuffer(nonce) },
      aesCryptoKey,
      toArrayBuffer(encryptedBytes)
    );

    return decryptedBuffer;
  } catch (error: any) {
    // Log minimal context only
    logger.error("decryptFileWithAES failed", {
      error: error?.message,
      bytes_len: encryptedBytes?.byteLength ?? 0,
      iv_len: nonce?.byteLength ?? 0,
    });
    throw error;
  }
};


// ---------------------------------------------------------------------
export const encryptFileWithAES = async (
  file: File
): Promise<{ encryptedFileBlob: Blob; aesKey: Uint8Array; nonce: Uint8Array }> => {
  const fileBuffer = await file.arrayBuffer();

  // Generate AES Key (256-bit)
  const aesKey = window.crypto.getRandomValues(new Uint8Array(32));
  const nonce = window.crypto.getRandomValues(new Uint8Array(12)); // AES-GCM standard
  const aesCryptoKey = await window.crypto.subtle.importKey(
    "raw",
    aesKey,
    "AES-GCM",
    false,
    ["encrypt"]
  );

  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce },
    aesCryptoKey,
    fileBuffer
  );

  const encryptedBlob = new Blob([encryptedBuffer], { type: "application/octet-stream" });

  return {
    encryptedFileBlob: encryptedBlob,
    aesKey,
    nonce,
  };
};


// ---------------------------------------------------------------------
export const encryptAESKeyForAllDevices = async (
  aesKey: Uint8Array,
  nonce: Uint8Array,
  deviceKeys: { device_id: string; public_key: string }[]
): Promise<{ device_id: string; encrypted_aes_key: string }[]> => {
  return await Promise.all(
    deviceKeys.map(async ({ device_id, public_key }) => {
      try {
        const encrypted_aes_key = await encryptAESKeyWithPublicKey(aesKey, nonce, public_key);
        return { device_id, encrypted_aes_key };
      } catch (err: any) {
        // Minimal, safe logging
        logger.error("Failed to encrypt AES key for device", {
          device_id,
          error: err?.message,
        });
        return { device_id, encrypted_aes_key: "[Encryption failed]" };
      }
    })
  );
};
